import client from '@/lib/axios-client'
import type {
	DDSInput,
	DDSListOutput,
	DDSMeListOutput,
	DDSMeListParams,
	DDSOutput,
	DDSUpdateInput,
} from '@/types'
import {
	assertUploadSizeWithinLimit,
	DEFAULT_MAX_SIZE_MB,
	optimizeImageForUpload,
	sendWithCompressionRetry,
} from '@/utils/image-compression'

const DDS_URL = '/dds'

export async function getDDSList(params?: {
	page?: number
	limit?: number
	branchId?: string
}): Promise<DDSListOutput> {
	const response = await client.get(DDS_URL, { params })
	return response.data.data
}

export async function getDDSMeList(
	params?: DDSMeListParams,
): Promise<DDSMeListOutput> {
	const response = await client.get(`${DDS_URL}/me`, { params })
	return response.data.data
}

export async function getDDSById(id: string): Promise<DDSOutput> {
	const response = await client.get(`${DDS_URL}/${id}`)
	return response.data.data
}

export async function exportDDSPdf(id: string): Promise<Blob> {
	const response = await client.get(`${DDS_URL}/${id}/export`, {
		responseType: 'blob',
	})
	return response.data
}

export async function createDDS(data: DDSInput): Promise<DDSOutput> {
	const send = async (maxSizeMB: number) => {
		const formData = new FormData()
		formData.append('crId', data.crId)
		if (data.customerId) {
			formData.append('customerId', data.customerId)
		}
		formData.append('branchId', data.branchId)
		formData.append('date', data.date)
		formData.append('subject', data.subject)
		formData.append('type', data.type)
		formData.append('description', data.description)
		data.attendeeIds.forEach((attendeeId) => {
			formData.append('attendeeIds', attendeeId)
		})
		formData.append('durationMinutes', String(data.durationMinutes))
		if (data.selfCheckIn) {
			formData.append('selfCheckIn', 'true')
		}

		if (data.image) {
			const optimizedImage = await optimizeImageForUpload(data.image, maxSizeMB)
			assertUploadSizeWithinLimit([optimizedImage])
			formData.append('image', optimizedImage)
		}

		const response = await client.post(DDS_URL, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		})
		return response.data.data
	}

	// Sem imagem, recomprimir nao resolve um 413 — envia sem retry
	return data.image instanceof File
		? sendWithCompressionRetry(send)
		: send(DEFAULT_MAX_SIZE_MB)
}

export async function updateDDS(
	id: string,
	data: DDSUpdateInput,
): Promise<DDSOutput> {
	const send = async (maxSizeMB: number) => {
		const formData = new FormData()

		if (data.customerId) {
			formData.append('customerId', data.customerId)
		}

		if (data.attendeeIds?.length) {
			data.attendeeIds.forEach((attendeeId) => {
				formData.append('attendeeIds', attendeeId)
			})
		}

		if (data.image) {
			const optimizedImage = await optimizeImageForUpload(data.image, maxSizeMB)
			assertUploadSizeWithinLimit([optimizedImage])
			formData.append('image', optimizedImage)
		}

		const response = await client.put(`${DDS_URL}/${id}`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		})
		return response.data.data
	}

	return data.image instanceof File
		? sendWithCompressionRetry(send)
		: send(DEFAULT_MAX_SIZE_MB)
}
